import { sfx } from '../audio';
import { settings, updateSettings, type ImageStyle } from '../settings';
import { el } from './dom';

let modal: HTMLElement | null = null;

function toggle(label: string, hint: string, value: boolean, onChange: (v: boolean) => void): HTMLElement {
  const row = el('label', 'setting');
  const input = el('input');
  input.type = 'checkbox';
  input.checked = value;
  input.addEventListener('change', () => onChange(input.checked));
  const text = el('div');
  text.append(el('div', 'setting-name', label), el('div', 'hint', hint));
  row.append(input, text);
  return row;
}

function styleChoice(): HTMLElement {
  const row = el('div', 'setting');
  const box = el('div');
  box.append(el('div', 'setting-name', 'Styl obrazu'));
  const options = el('div', 'style-options');
  const styles: [ImageStyle, string][] = [
    ['wyrazny', 'Wyraźny'],
    ['malowany', 'Malowany'],
    ['czysty', 'Czysty'],
  ];
  for (const [value, label] of styles) {
    const b = el('button', value === settings.style ? 'chip active' : 'chip', label);
    b.addEventListener('click', () => {
      updateSettings({ style: value });
      for (const other of options.children) other.classList.toggle('active', other === b);
    });
    options.append(b);
  }
  box.append(options, el('div', 'hint', 'Wyraźny: ostre krawędzie i kontury. Malowany: plamy farby. Czysty: bez filtra.'));
  row.append(box);
  return row;
}

/** Pełny ekran (tablet, telefon); przeglądarki bez Fullscreen API nie dostają przełącznika. */
function fullscreenToggle(): HTMLElement | null {
  const doc = document as Document & { webkitFullscreenEnabled?: boolean; webkitFullscreenElement?: Element | null; webkitExitFullscreen?: () => void };
  const root = document.documentElement as HTMLElement & { webkitRequestFullscreen?: () => void };
  if (!(doc.fullscreenEnabled || doc.webkitFullscreenEnabled)) return null;
  const isOn = () => !!(doc.fullscreenElement || doc.webkitFullscreenElement);
  return toggle('Pełny ekran', 'Ukrywa pasek przeglądarki.', isOn(), (v) => {
    if (v && !isOn()) (root.requestFullscreen?.() ?? root.webkitRequestFullscreen?.())?.catch?.(() => {});
    else if (!v && isOn()) (doc.exitFullscreen?.() ?? doc.webkitExitFullscreen?.())?.catch?.(() => {});
  });
}

/** Okno ustawień: styl obrazu, jakość, głośność. Wspólne dla obozu i pauzy. */
export function openSettings(): void {
  if (!modal) {
    modal = el('div', 'screen settings-screen');
    document.getElementById('ui')!.append(modal);
  }
  const box = el('div', 'panel-box frame settings-box');
  const volume = el('input');
  volume.type = 'range';
  volume.min = '0';
  volume.max = '1';
  volume.step = '0.05';
  volume.value = String(settings.volume);
  volume.addEventListener('input', () => updateSettings({ volume: Number(volume.value) }));
  volume.addEventListener('change', () => sfx.click());
  const volRow = el('label', 'setting');
  const volText = el('div');
  volText.append(el('div', 'setting-name', 'Efekty dźwiękowe'), volume);
  volRow.append(volText);
  const musicVol = el('input');
  musicVol.type = 'range';
  musicVol.min = '0';
  musicVol.max = '1';
  musicVol.step = '0.05';
  musicVol.value = String(settings.music);
  musicVol.addEventListener('input', () => updateSettings({ music: Number(musicVol.value) }));
  const musicRow = el('label', 'setting');
  const musicText = el('div');
  musicText.append(el('div', 'setting-name', 'Muzyka'), musicVol);
  musicRow.append(musicText);

  const close = el('button', 'btn', 'Zamknij');
  close.addEventListener('click', () => (modal!.hidden = true));
  box.append(
    el('h2', 'title-glow', 'Ustawienia'),
    styleChoice(),
    toggle('Cienie otoczenia', 'Głębia: przyciemnienia w zagłębieniach i przy ziemi.', settings.ao, (v) => updateSettings({ ao: v })),
    toggle('Wysoka jakość', 'Gładsze krawędzie; wyłącz, jeśli gra się tnie.', settings.highQuality, (v) => updateSettings({ highQuality: v })),
    toggle('Wstrząsy kamery', 'Kamera drga przy mocnych ciosach i wybuchach.', settings.shake, (v) => updateSettings({ shake: v })),
    ...[fullscreenToggle()].filter((n): n is HTMLElement => !!n),
    volRow,
    musicRow,
    close,
  );
  modal.replaceChildren(box);
  modal.hidden = false;
}

export function settingsButton(): HTMLButtonElement {
  const b = el('button', 'icon-btn', '⚙');
  b.title = 'Ustawienia';
  b.addEventListener('click', openSettings);
  return b;
}
